import { TipoSecaoPaginaCustomizada } from '../../../../model/apm/apm-enums';
import { FieldMap } from '../../components/field-config';

export type CustomPageSectionField = 'title' | 'subtitle' | 'content' | 'url' | 'image';
export type CustomPageItemField = 'title' | 'subtitle' | 'description' | 'url' | 'image' | 'icon' | 'openInNewTab' | 'file';

export interface CustomPageSectionTypeConfig {
    description: string;
    fields: FieldMap<CustomPageSectionField>;
    itemNoun?: string;
    itemFields?: FieldMap<CustomPageItemField>;
}

const title = { label: 'Título', kind: 'text' as const };
const subtitle = { label: 'Subtítulo', kind: 'text' as const };
const itemTitle = { label: 'Título', kind: 'text' as const, required: true };
const image = { label: 'Imagem', kind: 'image' as const, requiredMessage: 'Imagem é obrigatória' };
const link = { label: 'Link', kind: 'url' as const, helper: 'Endereço para onde o item aponta (opcional)' };

export const CUSTOM_PAGE_SECTION_CONFIG: Record<TipoSecaoPaginaCustomizada, CustomPageSectionTypeConfig> = {
    [TipoSecaoPaginaCustomizada.TextoLivre]: {
        description: 'Bloco de texto formatado.',
        fields: { title, subtitle, content: { label: 'Conteúdo', kind: 'richText', required: true } },
    },
    [TipoSecaoPaginaCustomizada.ListaItens]: {
        description: 'Lista de itens com título, descrição, link e imagem opcionais.',
        fields: { title, subtitle, content: { label: 'Texto introdutório', kind: 'richText' } },
        itemNoun: 'Item',
        itemFields: {
            title: itemTitle,
            subtitle,
            description: { label: 'Descrição', kind: 'multiline' },
            url: link,
            image,
        },
    },
    [TipoSecaoPaginaCustomizada.GaleriaImagens]: {
        description: 'Galeria de imagens com legenda.',
        fields: { title, subtitle },
        itemNoun: 'Imagem',
        itemFields: {
            image: { ...image, required: true },
            title: itemTitle,
            description: { label: 'Legenda', kind: 'multiline' },
        },
    },
    [TipoSecaoPaginaCustomizada.Faq]: {
        description: 'Perguntas frequentes com respostas.',
        fields: { title, subtitle },
        itemNoun: 'Pergunta',
        itemFields: {
            title: { label: 'Pergunta', kind: 'text', required: true, requiredMessage: 'Pergunta é obrigatória' },
            description: { label: 'Resposta', kind: 'multiline', required: true, requiredMessage: 'Resposta é obrigatória' },
        },
    },
    [TipoSecaoPaginaCustomizada.ConteudoMisto]: {
        description: 'Texto com imagem e link, seguido de itens em destaque.',
        fields: {
            title, subtitle,
            content: { label: 'Conteúdo', kind: 'richText' },
            image,
            url: link,
        },
        itemNoun: 'Item',
        itemFields: {
            title: itemTitle,
            subtitle,
            description: { label: 'Descrição', kind: 'richText' },
            url: link,
            image,
        },
    },
    [TipoSecaoPaginaCustomizada.VideoEmbed]: {
        description: 'Vídeo do YouTube incorporado à página.',
        fields: {
            title, subtitle,
            url: { label: 'URL do vídeo (YouTube)', kind: 'youtube', required: true, requiredMessage: 'URL do vídeo é obrigatória' },
            content: { label: 'Descrição', kind: 'multiline' },
        },
    },
    [TipoSecaoPaginaCustomizada.ListaLinks]: {
        description: 'Lista de links com ícone, como atalhos para serviços e documentos.',
        fields: { title, subtitle },
        itemNoun: 'Link',
        itemFields: {
            icon: { label: 'Ícone', kind: 'icon', required: true },
            title: { label: 'Descrição', kind: 'text', required: true, requiredMessage: 'Descrição é obrigatória' },
            url: { label: 'Link', kind: 'url', required: true },
            openInNewTab: { label: 'Abrir em nova aba', kind: 'checkbox' },
        },
    },
    [TipoSecaoPaginaCustomizada.Documentos]: {
        description: 'Lista de documentos para download, como editais, relatórios e formulários.',
        fields: { title, subtitle },
        itemNoun: 'Documento',
        itemFields: {
            file: { label: 'Arquivo', kind: 'file', required: true },
            title: itemTitle,
            description: { label: 'Descrição', kind: 'multiline' },
        },
    },
};

export const customPageSectionHasItems = (type: TipoSecaoPaginaCustomizada) => !!CUSTOM_PAGE_SECTION_CONFIG[type]?.itemFields;
